import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

// ---------------------------------------------------------------------------
// Icones (inline : aucune requete reseau, contrainte du 100 % hors ligne)
// ---------------------------------------------------------------------------

const T = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.7, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const }

export function IconeAujourdhui() {
  return (
    <svg viewBox="0 0 24 24" {...T} aria-hidden="true">
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5 9.5V20h14V9.5" />
      <path d="M9.5 20v-6h5v6" />
    </svg>
  )
}

export function IconeJournal() {
  return (
    <svg viewBox="0 0 24 24" {...T} aria-hidden="true">
      <path d="M5 3h11l3 3v15H5z" />
      <path d="M8.5 8.5h7M8.5 12h7M8.5 15.5h4" />
    </svg>
  )
}

export function IconeRecettes() {
  return (
    <svg viewBox="0 0 24 24" {...T} aria-hidden="true">
      <path d="M4 12h16" />
      <path d="M5 12a7 7 0 0 1 14 0" />
      <path d="M4 16h16" />
      <path d="M9 5.5c0-1 1.5-1 1.5-2M13.5 5.5c0-1 1.5-1 1.5-2" />
    </svg>
  )
}

export function IconeAnalyses() {
  return (
    <svg viewBox="0 0 24 24" {...T} aria-hidden="true">
      <path d="M4 20V4" />
      <path d="M4 20h16" />
      <path d="M8 20v-6M12.5 20V9M17 20v-9" />
    </svg>
  )
}

export function IconeReglages() {
  return (
    <svg viewBox="0 0 24 24" {...T} aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3v2.5M12 18.5V21M3 12h2.5M18.5 12H21M5.6 5.6l1.8 1.8M16.6 16.6l1.8 1.8M18.4 5.6l-1.8 1.8M7.4 16.6l-1.8 1.8" />
    </svg>
  )
}

export function IconeChevron() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" {...T} aria-hidden="true">
      <path d="m9 5 7 7-7 7" />
    </svg>
  )
}

export function IconePlus() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" {...T} aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  )
}

export function IconeRetour() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" {...T} aria-hidden="true">
      <path d="m14 5-7 7 7 7" />
    </svg>
  )
}

export function IconeCroix() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" {...T} aria-hidden="true">
      <path d="m6 6 12 12M18 6 6 18" />
    </svg>
  )
}

// ---------------------------------------------------------------------------
// Structure de page
// ---------------------------------------------------------------------------

export function Entete({
  titre,
  sousTitre,
  retour,
  action,
}: {
  titre: string
  sousTitre?: string
  retour?: boolean
  action?: ReactNode
}) {
  const navigate = useNavigate()
  return (
    <header className="entete">
      {retour && (
        <button
          type="button"
          className="btn btn--fantome"
          style={{ padding: 4, minHeight: 36 }}
          onClick={() => navigate(-1)}
          aria-label="Retour"
        >
          <IconeRetour />
        </button>
      )}
      <div className="entete__titre">
        <h1>{titre}</h1>
        {sousTitre && <span className="entete__sous">{sousTitre}</span>}
      </div>
      {action}
    </header>
  )
}

export function Carte({
  titre,
  action,
  children,
  plate,
}: {
  titre?: string
  action?: ReactNode
  children: ReactNode
  plate?: boolean
}) {
  return (
    <section className={plate ? 'carte carte--plate' : 'carte'}>
      {(titre || action) && (
        <div className="carte__titre">
          {titre && <h2>{titre}</h2>}
          {action}
        </div>
      )}
      {children}
    </section>
  )
}

export function Vide({ children }: { children: ReactNode }) {
  return <div className="vide">{children}</div>
}

// ---------------------------------------------------------------------------
// Formulaire
// ---------------------------------------------------------------------------

export function Champ({
  label,
  aide,
  children,
}: {
  label: string
  aide?: string
  children: ReactNode
}) {
  return (
    <label className="champ">
      <span className="champ__label">{label}</span>
      {children}
      {aide && <span className="champ__aide">{aide}</span>}
    </label>
  )
}

/** Curseur 0-10 avec la valeur affichee. */
export function Curseur({
  label,
  valeur,
  onChange,
  aide,
  min = 0,
  max = 10,
  pas = 1,
  suffixe = '',
}: {
  label: string
  valeur: number
  onChange: (v: number) => void
  aide?: string
  min?: number
  max?: number
  pas?: number
  suffixe?: string
}) {
  return (
    <div className="champ">
      <span className="champ__label ligne ligne--espacee">
        <span>{label}</span>
        <span className="mono gras" style={{ color: 'var(--brun)' }}>
          {valeur}
          {suffixe}
        </span>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={pas}
        value={valeur}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      {aide && <span className="champ__aide">{aide}</span>}
    </div>
  )
}

/**
 * Champ numerique controle.
 *
 * Un `<input type="number">` pilote directement par un nombre est piegeux :
 * `Number('')` vaut `0`, donc vider la case y replace aussitot un « 0 »
 * impossible a effacer, et tout ce qui est tape ensuite s'y accole
 * (« 200 » devient « 0200 »).
 *
 * La correction consiste a garder le **texte saisi** comme source de verite de
 * l'affichage, et a ne remonter au parent qu'une valeur reellement numerique.
 * Le champ peut donc rester vide le temps de la frappe sans que l'etat du
 * parent ne soit corrompu ; la sortie du champ normalise ce qui est affiche.
 */
export function ChampNombre({
  valeur,
  onChange,
  min,
  step,
  decimal,
  ariaLabel,
  style,
}: {
  valeur: number
  onChange: (v: number) => void
  min?: number
  step?: number
  decimal?: boolean
  ariaLabel?: string
  style?: React.CSSProperties
}) {
  const [texte, setTexte] = useState(() => String(valeur))
  // Derniere valeur emise vers le parent : sert a distinguer un changement
  // venu de l'exterieur (chargement d'un repas) d'un echo de notre propre
  // saisie, qui ne doit surtout pas ecraser ce que l'utilisateur tape.
  const dernierEmis = useRef(valeur)

  useEffect(() => {
    if (valeur !== dernierEmis.current) {
      dernierEmis.current = valeur
      setTexte(String(valeur))
    }
  }, [valeur])

  return (
    <input
      type="number"
      inputMode={decimal ? 'decimal' : 'numeric'}
      min={min}
      step={step}
      value={texte}
      aria-label={ariaLabel}
      style={style}
      onChange={(e) => {
        const t = e.target.value
        setTexte(t)
        const n = Number(t)
        // Champ vide ou saisie encore incomplete (« - », « 1. ») : on conserve
        // la derniere valeur valide cote parent sans rien reafficher.
        if (t.trim() !== '' && Number.isFinite(n)) {
          dernierEmis.current = n
          onChange(n)
        }
      }}
      onBlur={() => {
        // Normalise l'affichage a la sortie : restaure la valeur si le champ a
        // ete laisse vide, et supprime un eventuel zero de tete.
        setTexte(String(valeur))
      }}
    />
  )
}

// ---------------------------------------------------------------------------
// Confirmation
// ---------------------------------------------------------------------------

/**
 * Remplace `window.confirm`, qui est bloque ou incoherent selon les navigateurs
 * en mode standalone iOS.
 */
export function Confirmation({
  titre,
  message,
  libelleConfirmer = 'Confirmer',
  danger,
  onConfirmer,
  onAnnuler,
}: {
  titre: string
  message: string
  libelleConfirmer?: string
  danger?: boolean
  onConfirmer: () => void
  onAnnuler: () => void
}) {
  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onAnnuler()
    }
    document.addEventListener('keydown', onEsc)
    return () => document.removeEventListener('keydown', onEsc)
  }, [onAnnuler])

  return (
    <div className="voile" role="dialog" aria-modal="true" aria-label={titre} onClick={onAnnuler}>
      <div className="modale" onClick={(e) => e.stopPropagation()}>
        <h2>{titre}</h2>
        <p className="doux">{message}</p>
        <div className="boutons" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn" onClick={onAnnuler}>
            Annuler
          </button>
          <button
            type="button"
            className={danger ? 'btn btn--danger' : 'btn btn--principal'}
            onClick={onConfirmer}
          >
            {libelleConfirmer}
          </button>
        </div>
      </div>
    </div>
  )
}

/** Bouton qui demande confirmation avant d'agir. */
export function BoutonSupprimer({
  onSupprimer,
  titre,
  message,
  libelle = 'Supprimer',
}: {
  onSupprimer: () => void
  titre: string
  message: string
  libelle?: string
}) {
  const [ouvert, setOuvert] = useState(false)
  return (
    <>
      <button type="button" className="btn btn--danger" onClick={() => setOuvert(true)}>
        {libelle}
      </button>
      {ouvert && (
        <Confirmation
          titre={titre}
          message={message}
          libelleConfirmer={libelle}
          danger
          onConfirmer={() => {
            setOuvert(false)
            onSupprimer()
          }}
          onAnnuler={() => setOuvert(false)}
        />
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Divers
// ---------------------------------------------------------------------------

export function Puce({
  children,
  ton = 'neutre',
}: {
  children: ReactNode
  ton?: 'neutre' | 'ok' | 'alerte' | 'danger' | 'info'
}) {
  const cls = ton === 'neutre' ? 'puce' : `puce puce--${ton}`
  return <span className={cls}>{children}</span>
}

export function Jauge({ valeur }: { valeur: number }) {
  return (
    <div className="jauge" role="presentation">
      <div
        className="jauge__remplissage"
        style={{ width: `${Math.max(0, Math.min(100, valeur))}%` }}
      />
    </div>
  )
}

/** Charge une valeur asynchrone et rend un etat de chargement le temps voulu. */
export function useAsync<T>(
  fn: () => Promise<T>,
  deps: unknown[],
): { valeur: T | undefined; chargement: boolean; recharger: () => void } {
  const [valeur, setValeur] = useState<T | undefined>(undefined)
  const [chargement, setChargement] = useState(true)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    let annule = false
    setChargement(true)
    fn()
      .then((v) => {
        if (!annule) {
          setValeur(v)
          setChargement(false)
        }
      })
      .catch((e) => {
        console.error(e)
        if (!annule) setChargement(false)
      })
    return () => {
      annule = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick])

  return { valeur, chargement, recharger: () => setTick((t) => t + 1) }
}
